import clsx from 'clsx';
import type React from 'react';

import { Icon } from '@gitbook/spine-icons';
import { AtomButton } from '@gitbook/spine-react';

import { AgentDockButton } from './AgentDockButton';

/**
 * How long to hover a tab before its title appears.
 *
 * Longer than the default: only the tabs a crowded bar has cut off carry a tooltip at all, but a
 * crowded bar is precisely when there are several of them in a row to sweep the pointer across.
 */
const TAB_TOOLTIP_DELAY_MS = 700;

/**
 * A conversation tab in the dock.
 *
 * A button cannot contain a button, so the close control is a sibling layered over the tab's
 * trailing edge, with a gradient fading the title out beneath it. That gradient is the reason the
 * dock has its own button: it resolves to the same opaque fill the button paints, so it genuinely
 * covers the title. Spine's `blank` fills with an alpha neutral, which no gradient can match —
 * fading to a translucent colour tints the text rather than hiding it.
 */
export function AgentDockTab(props: {
    title: string;
    active: boolean;
    /**
     * What the conversation is up to: `question` marks the tab with a "!" — the agent has stopped
     * to ask the user something — and `processing` shimmers the title while it works.
     */
    status?: 'processing' | 'question';
    /**
     * A glyph marking what kind of conversation this is, for the ones that are not simply the
     * user's own. Given up to the attention marker while the agent is waiting on an answer, which
     * is the more urgent thing the same slot can say.
     */
    icon?: React.ReactNode;
    /** The tab's box, measured by the dock to align the conversation window with it. */
    anchorRef?: React.Ref<HTMLDivElement>;
    onSelect: () => void;
    onClose: () => void;
}) {
    const { title, active, status, icon, anchorRef, onSelect, onClose } = props;
    // The tab only reports what the user cannot already see: with its window on screen, the
    // conversation speaks for itself. Minimizing the dock deactivates every tab, so the report
    // comes back the moment the window goes away.
    const reported = active ? undefined : status;

    return (
        // No width floor: one would inflate a short title with dead space on its trailing edge.
        // A crowded bar shrinks tabs freely instead, and the truncated title carries a tooltip.
        <div ref={anchorRef} className="group relative flex min-w-0 shrink">
            <AgentDockButton
                label={title}
                active={active}
                // Filled from the group, so the tab keeps its fill while the pointer is on the
                // close control layered over it.
                fillOnGroupHover
                // Tabs share the row: each gives up width as the bar fills, truncating its
                // title rather than overflowing.
                shrinkable
                // The shimmer says the agent is working in there, sweeping a band across the
                // title itself — a minimized tab has no other way to show it. The band is the
                // color of the surface behind the bar, so the letters visibly fade into it as it
                // passes; the default sheen (a brighter text color) is too quiet on text this
                // small.
                labelClassName={clsx(
                    'min-w-0 font-medium',
                    reported === 'processing' &&
                        'shimmer-text [--shimmer-text-sheen:var(--sp-color-bg-side)]'
                )}
                // The title is on the tab already, so it is only worth repeating for a tab the bar
                // has squeezed too narrow to read — which the label reports for itself.
                labelTooltipProps={{ delay: TAB_TOOLTIP_DELAY_MS }}
                icon={reported === 'question' ? <AgentDockTabAttention /> : icon}
                onPress={onSelect}
                closable
            >
                {title}
            </AgentDockButton>
            {/* The gradient resolves to the button's own fill, so the title disappears under the
                close control instead of showing through it. The control is always in the tab
                order, so it is also revealed when it is itself keyboard-focused — but not when the
                tab is, which only takes the hover fill. */}
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center rounded-r-md bg-linear-to-r from-transparent to-neutral-ui-active to-40% pr-1.5 pl-6 opacity-0 transition-opacity group-hover:opacity-100 has-[:focus-visible]:opacity-100">
                <AtomButton
                    onPress={onClose}
                    aria-label={`Close ${title}`}
                    // Only the glyph reacts to its own hover — the tab underneath is already filled
                    // by the group, and its hover and active fills are the same colour, so nothing
                    // shifts while reaching for the X. Full strength once focused, so the keyboard
                    // user sees where they are rather than a half-faded glyph.
                    className="pointer-events-auto flex size-4 items-center justify-center rounded-xs text-base opacity-60 transition-opacity hover:opacity-100 data-focus-visible:opacity-100 data-focus-visible:focus-ring"
                >
                    <Icon.X className="size-3.5" />
                </AtomButton>
            </div>
        </div>
    );
}

/**
 * The "!" telling the user this conversation is waiting on them. A text glyph rather than an icon:
 * it sits on the label's own baseline at the label's own size, with none of an icon box's padding
 * to fight in a control this small.
 */
function AgentDockTabAttention() {
    return (
        <span
            role="img"
            aria-label="Waiting for your answer"
            className="shrink-0 font-semibold text-warning"
        >
            !
        </span>
    );
}
